import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import {
  SUPPORTED_LANGS,
  LANG_LABELS_FULL,
  LANG_FLAGS,
  getStoredLang,
  switchLanguage,
} from '../i18n/langDetect';

/**
 * LangPill, compact dropdown language switcher used across the app shell,
 * login pages and marketing header. Writes to the shared localStorage key
 * (`keystone_app_lang`) then reloads so the whole tree re-renders with the
 * new language (marketing bundles + DomTranslator both key off that value).
 *
 * Variants:
 *   - `light`  (default), dark pill for cream/light backgrounds
 *   - `dark`            , cream pill for navy backgrounds (used on Login /
 *                          LoginManage / marketing footer)
 *   - `subtle`          , borderless, tiny, for compact bars
 */
export default function LangPill({ variant = 'light', className = '', style = {} }) {
  const [open, setOpen] = useState(false);
  const current = getStoredLang() || 'en';
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const palette = variant === 'dark' ? {
    bg: 'rgba(255,255,255,0.08)',
    bgOpen: 'rgba(255,255,255,0.14)',
    fg: '#fff',
    border: 'rgba(255,255,255,0.15)',
    menuBg: '#0F1B2A',
    menuFg: '#E5E7EB',
    hover: 'rgba(255,255,255,0.08)',
    active: 'rgba(233,30,99,0.20)',
    activeFg: '#F472B6',
  } : variant === 'subtle' ? {
    bg: 'transparent',
    bgOpen: 'rgba(10,37,64,0.06)',
    fg: '#0A2540',
    border: 'transparent',
    menuBg: '#fff',
    menuFg: '#0F1B2A',
    hover: 'rgba(10,37,64,0.05)',
    active: 'rgba(233,30,99,0.10)',
    activeFg: '#C2185B',
  } : {
    bg: '#fff',
    bgOpen: '#F5F1E8',
    fg: '#0A2540',
    border: '#E5E1D8',
    menuBg: '#fff',
    menuFg: '#0F1B2A',
    hover: '#F5F1E8',
    active: 'rgba(233,30,99,0.10)',
    activeFg: '#C2185B',
  };

  return (
    <div ref={ref} className={className} style={{ position: 'relative', display: 'inline-block', ...style }} data-testid="langpill">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="langpill-trigger"
        aria-label="Change language"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: open ? palette.bgOpen : palette.bg,
          color: palette.fg,
          border: `1px solid ${palette.border}`,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.02em',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
      >
        <Globe size={15} strokeWidth={2.1} />
        <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden="true">{LANG_FLAGS[current]}</span>
        <span style={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{current}</span>
        <ChevronDown size={13} style={{ opacity: 0.7 }} />
      </button>
      {open && (
        <div
          data-testid="langpill-menu"
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 168,
            background: palette.menuBg,
            border: `1px solid ${palette.border}`,
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          {SUPPORTED_LANGS.map((l) => {
            const active = l === current;
            return (
              <button
                key={l}
                type="button"
                onClick={() => { if (l !== current) switchLanguage(l); else setOpen(false); }}
                data-testid={`langpill-option-${l}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 14px',
                  background: active ? palette.active : 'transparent',
                  color: active ? palette.activeFg : palette.menuFg,
                  border: 'none',
                  fontSize: 13.5,
                  fontWeight: active ? 700 : 500,
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = palette.hover; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 17, lineHeight: 1 }} aria-hidden="true">{LANG_FLAGS[l]}</span>
                <span>{LANG_LABELS_FULL[l]}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.55 }}>{l}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
